import { useState, useEffect, useRef } from "react";
import { Save, Building2, Loader2, Upload, Palette, Image, X, PenTool, Camera, CreditCard, CheckCircle2, AlertCircle, ExternalLink, Trash2, Users, Plus, UserMinus, Mail, RefreshCw, Monitor, Check, Globe, MessageSquare, Send } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import SignaturePad from "../components/SignaturePad";
import { useTheme, platformThemes } from "../contexts/ThemeContext";
import { useLanguage, LANGUAGES } from "../context/LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const currencies = [
  { code: "USD", name: "US Dollar", symbol: "$" },
  { code: "LRD", name: "Liberian Dollar", symbol: "L$" },
  { code: "EUR", name: "Euro", symbol: "€" },
  { code: "GBP", name: "British Pound", symbol: "£" },
  { code: "CAD", name: "Canadian Dollar", symbol: "C$" },
  { code: "AUD", name: "Australian Dollar", symbol: "A$" },
  { code: "INR", name: "Indian Rupee", symbol: "₹" },
  { code: "KES", name: "Kenyan Shilling", symbol: "KSh" },
  { code: "NGN", name: "Nigerian Naira", symbol: "₦" },
  { code: "GHS", name: "Ghanaian Cedi", symbol: "GH₵" },
  { code: "ZAR", name: "South African Rand", symbol: "R" },
  { code: "JPY", name: "Japanese Yen", symbol: "¥" },
  { code: "CNY", name: "Chinese Yuan", symbol: "¥" },
  { code: "XOF", name: "West African CFA", symbol: "CFA" },
];

const presetColors = [
  { name: "Emerald", primary: "#047857", secondary: "#B49248" },
  { name: "Ocean Blue", primary: "#0369A1", secondary: "#F59E0B" },
  { name: "Royal Purple", primary: "#7C3AED", secondary: "#EC4899" },
  { name: "Crimson", primary: "#DC2626", secondary: "#1F2937" },
  { name: "Slate", primary: "#475569", secondary: "#0EA5E9" },
  { name: "Forest", primary: "#166534", secondary: "#CA8A04" },
  { name: "Midnight", primary: "#1E3A5F", secondary: "#F97316" },
  { name: "Charcoal", primary: "#374151", secondary: "#10B981" },
];

export default function Settings() {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [subscription, setSubscription] = useState(null);
  const [loadingSubscription, setLoadingSubscription] = useState(true);
  const [managingBilling, setManagingBilling] = useState(false);
  const { language, setLanguage, t, languages, detectedCountry } = useLanguage();
  const [resettingAccount, setResettingAccount] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [pendingInvitations, setPendingInvitations] = useState([]);
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [inviteRole, setInviteRole] = useState("bookkeeper");
  const [inviting, setInviting] = useState(false);
  const [customPrimaryColor, setCustomPrimaryColor] = useState("#047857");
  const [customAccentColor, setCustomAccentColor] = useState("#D4B068");
  const fileInputRef = useRef(null);
  
  // Support ticket states
  const [supportTickets, setSupportTickets] = useState([]);
  const [loadingTickets, setLoadingTickets] = useState(false);
  const [showTicketDialog, setShowTicketDialog] = useState(false);
  const [ticketSubject, setTicketSubject] = useState("");
  const [ticketMessage, setTicketMessage] = useState("");
  const [ticketPriority, setTicketPriority] = useState("normal");
  const [submittingTicket, setSubmittingTicket] = useState(false);
  
  // Theme context
  const { currentTheme, setTheme, setCustomTheme, themes } = useTheme();
  
  const [profile, setProfile] = useState({
    name: "",
    email: "",
    phone: "",
    address: "",
    logo_url: "",
    logo_data: "",
    tax_id: "",
    default_currency: "USD",
    reporting_currency: "USD", // For Liberia: can report in USD or LRD
    default_tax_rate: 0,
    default_terms: "",
    default_notes: "",
    brand_primary_color: "#047857",
    brand_secondary_color: "#B49248",
    signature_data: ""
  });

  useEffect(() => {
    loadProfile();
    loadSubscription();
    loadTeamMembers();
    loadSupportTickets();
  }, []);

  const loadSupportTickets = async () => {
    setLoadingTickets(true);
    try {
      const response = await axios.get(`${API}/support/my-tickets`);
      setSupportTickets(response.data.tickets || []);
    } catch (error) {
      console.error("Failed to load support tickets:", error);
    } finally {
      setLoadingTickets(false);
    }
  };

  const submitSupportTicket = async () => {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      toast.error("Please provide both subject and message");
      return;
    }
    setSubmittingTicket(true);
    try {
      await axios.post(`${API}/support/tickets`, {
        subject: ticketSubject,
        message: ticketMessage,
        priority: ticketPriority
      });
      toast.success("Support ticket submitted! We'll respond via email.");
      setShowTicketDialog(false);
      setTicketSubject("");
      setTicketMessage("");
      setTicketPriority("normal");
      loadSupportTickets();
    } catch (error) {
      toast.error("Failed to submit ticket. Please try again.");
    } finally {
      setSubmittingTicket(false);
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await axios.get(`${API}/team/members`);
      setTeamMembers(response.data.members || []);
      setPendingInvitations(response.data.pending_invitations || []);
    } catch (error) {
      console.error("Failed to load team members:", error);
      setTeamMembers([]);
      setPendingInvitations([]);
    } finally {
      setLoadingTeam(false);
    }
  };

  const inviteTeamMember = async () => {
    if (!inviteEmail) return;
    setInviting(true);
    
    // Map roles to permissions - Bookkeeper has restricted access
    const rolePermissions = {
      admin: ["create_documents", "view_documents", "manage_clients", "view_financials", "access_settings", "manage_inventory"],
      bookkeeper: ["create_documents", "view_documents", "manage_clients", "manage_inventory", "quick_sale"],
      viewer: ["view_documents"]
    };
    
    try {
      await axios.post(`${API}/team/invite`, {
        email: inviteEmail,
        name: inviteName,
        role: inviteRole,
        permissions: rolePermissions[inviteRole] || ["view_documents"]
      });
      toast.success(`Invitation sent to ${inviteEmail}`);
      setShowInviteDialog(false);
      setInviteEmail("");
      setInviteName("");
      setInviteRole("bookkeeper");
      loadTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to send invitation");
    } finally {
      setInviting(false);
    }
  };

  const removeTeamMember = async (memberId) => {
    if (!confirm("Are you sure you want to remove this team member?")) return;
    try {
      await axios.delete(`${API}/team/members/${memberId}`);
      toast.success("Team member removed");
      loadTeamMembers();
    } catch (error) {
      toast.error("Failed to remove team member");
    }
  };

  const resendInvitation = async (invitationId) => {
    try {
      await axios.post(`${API}/team/resend-invitation/${invitationId}`);
      toast.success("Invitation email resent");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to resend invitation");
    }
  };

  const cancelInvitation = async (invitationId) => {
    if (!confirm("Are you sure you want to cancel this invitation?")) return;
    try {
      await axios.delete(`${API}/team/invitations/${invitationId}`);
      toast.success("Invitation cancelled");
      loadTeamMembers();
    } catch (error) {
      toast.error("Failed to cancel invitation");
    }
  };

  const loadSubscription = async () => {
    try {
      setLoadingSubscription(true);
      const response = await axios.get(`${API}/subscription/status`);
      setSubscription(response.data);
    } catch (error) {
      console.error("Failed to load subscription:", error);
    } finally {
      setLoadingSubscription(false);
    }
  };

  const handleManageBilling = async () => {
    setManagingBilling(true);
    try {
      const response = await axios.post(`${API}/subscription/create-portal`);
      if (response.data.portal_url) {
        window.location.href = response.data.portal_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to open billing portal");
      setManagingBilling(false);
    }
  };

  const handleSubscribe = (plan) => {
    window.location.href = `/pricing?plan=${plan}`;
  };

  const handleResetAccount = async () => {
    if (!window.confirm("WARNING: This will permanently delete ALL your transactions, documents, clients, and inventory. This cannot be undone. Are you sure?")) {
      return;
    }
    if (!window.confirm("FINAL WARNING: All your data will be permanently deleted. Type 'RESET' in the next prompt to confirm.")) {
      return;
    }
    const confirmText = window.prompt("Type RESET to confirm:");
    if (confirmText !== "RESET") {
      toast.error("Reset cancelled - confirmation text did not match");
      return;
    }
    
    setResettingAccount(true);
    try {
      const response = await axios.post(`${API}/account/reset`);
      toast.success("Account reset successfully. All data has been cleared.");
      // Reload the page to refresh all data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset account");
    } finally {
      setResettingAccount(false);
    }
  };

  const loadProfile = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/business-profile`);
      if (response.data) {
        const data = response.data;
        setProfile({
          name: data.business_name || data.name || "",
          email: data.email || "",
          phone: data.phone || "",
          address: data.address || "",
          logo_url: data.logo_url || "",
          logo_data: data.logo_data || "",
          tax_id: data.tax_id || "",
          default_currency: data.default_currency || "USD",
          reporting_currency: data.reporting_currency || data.default_currency || "USD",
          default_tax_rate: data.default_tax_rate || 0,
          default_terms: data.default_terms || "",
          default_notes: data.default_footer_note || data.default_notes || "",
          brand_primary_color: data.primary_color || data.brand_primary_color || "#047857",
          brand_secondary_color: data.brand_secondary_color || "#B49248",
          signature_data: data.signature_data || ""
        });
      }
    } catch (error) {
      console.error("Failed to load profile:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 500000) {
      toast.error("Logo must be under 500KB");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setProfile(prev => ({
        ...prev,
        logo_data: event.target.result,
        logo_url: "" // Clear URL when uploading file
      }));
      toast.success("Logo uploaded!");
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setProfile(prev => ({ ...prev, logo_data: "", logo_url: "" }));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const applyPresetColor = (preset) => {
    setProfile(prev => ({
      ...prev,
      brand_primary_color: preset.primary,
      brand_secondary_color: preset.secondary
    }));
    toast.success(`Applied ${preset.name} theme`);
  };

  const saveProfile = async () => {
    setSaving(true);
    try {
      // Save to business-profile endpoint with proper field mapping
      await axios.put(`${API}/business-profile`, {
        business_name: profile.name,
        email: profile.email,
        phone: profile.phone,
        address: profile.address,
        logo_url: profile.logo_url,
        logo_data: profile.logo_data,
        tax_id: profile.tax_id,
        default_currency: profile.default_currency,
        reporting_currency: profile.reporting_currency,
        default_tax_rate: profile.default_tax_rate,
        default_terms: profile.default_terms,
        default_footer_note: profile.default_notes,
        primary_color: profile.brand_primary_color,
        brand_secondary_color: profile.brand_secondary_color,
        signature_data: profile.signature_data
      });
      toast.success("Settings saved successfully!");
    } catch (error) {
      toast.error("Failed to save settings");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto animate-slide-in" data-testid="settings-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-heading font-bold text-foreground">
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure your business profile and default document settings
        </p>
      </div>

      {/* Subscription Status */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
            <CreditCard className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground">
              Subscription
            </h2>
            <p className="text-sm text-muted-foreground">
              Manage your LedgerAMT subscription
            </p>
          </div>
        </div>

        {loadingSubscription ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading subscription...
          </div>
        ) : subscription?.is_subscribed ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-emerald-50 dark:bg-emerald-950/30 rounded-lg border border-emerald-200 dark:border-emerald-800">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                <div>
                  <p className="font-semibold text-emerald-700 dark:text-emerald-400">
                    LedgerAMT Pro - {subscription.plan === "yearly" ? "Yearly" : "Monthly"}
                  </p>
                  <p className="text-sm text-emerald-600/80 dark:text-emerald-400/80">
                    {subscription.cancel_at_period_end 
                      ? `Cancels on ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : "N/A"}`
                      : `Renews on ${subscription.current_period_end ? new Date(subscription.current_period_end).toLocaleDateString() : "N/A"}`
                    }
                  </p>
                </div>
              </div>
              <Button 
                variant="outline" 
                onClick={handleManageBilling}
                disabled={managingBilling}
                data-testid="manage-billing-btn"
              >
                {managingBilling ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <ExternalLink className="w-4 h-4 mr-2" />
                )}
                Manage Billing
              </Button>
            </div>
            
            <div className="text-sm text-muted-foreground">
              <strong>Included features:</strong>
              <ul className="mt-2 grid grid-cols-2 gap-1">
                {subscription.features?.map((feature, i) => (
                  <li key={i} className="flex items-center gap-1">
                    <CheckCircle2 className="w-3 h-3 text-emerald-500" />
                    {feature}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 p-4 bg-amber-50 dark:bg-amber-950/30 rounded-lg border border-amber-200 dark:border-amber-800">
            <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-amber-700 dark:text-amber-400">
                No Active Subscription
              </p>
              <p className="text-sm text-amber-600/80 dark:text-amber-400/80">
                Subscriptions are managed by your administrator. Please contact your admin to activate your account.
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Language Settings */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Globe className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground">
              {t('settings.languageSettings')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.languageDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div>
            <Label>{t('settings.selectLanguage')}</Label>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-full mt-1.5" data-testid="language-selector">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((lang) => (
                  <SelectItem key={lang.code} value={lang.code}>
                    <span className="flex items-center gap-2">
                      <span>{lang.nativeName}</span>
                      <span className="text-muted-foreground">({lang.name})</span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {detectedCountry && (
              <p className="text-xs text-muted-foreground mt-2">
                {t('settings.autoDetected')} ({detectedCountry})
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Business Profile */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Building2 className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground">
              {t('settings.businessProfile')}
            </h2>
            <p className="text-sm text-muted-foreground">
              {t('settings.languageDescription')}
            </p>
          </div>
        </div>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <Label>{t('settings.businessName')}</Label>
              <Input
                value={profile.name}
                onChange={(e) => setProfile(prev => ({ ...prev, name: e.target.value }))}
                placeholder={t('settings.businessName')}
                data-testid="profile-name-input"
              />
            </div>
            <div>
              <Label>{t('common.email')}</Label>
              <Input
                type="email"
                value={profile.email}
                onChange={(e) => setProfile(prev => ({ ...prev, email: e.target.value }))}
                placeholder="email@company.com"
                data-testid="profile-email-input"
              />
            </div>
            <div>
              <Label>Phone</Label>
              <Input
                value={profile.phone}
                onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                placeholder="+1 234 567 8900"
                data-testid="profile-phone-input"
              />
            </div>
            <div className="md:col-span-2">
              <Label>Address</Label>
              <Textarea
                value={profile.address}
                onChange={(e) => setProfile(prev => ({ ...prev, address: e.target.value }))}
                placeholder="123 Business St, City, State, Country"
                rows={2}
                data-testid="profile-address-input"
              />
            </div>
            <div>
              <Label>Tax ID / VAT Number</Label>
              <Input
                value={profile.tax_id}
                onChange={(e) => setProfile(prev => ({ ...prev, tax_id: e.target.value }))}
                placeholder="XX-XXXXXXX"
                data-testid="profile-taxid-input"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Brand Logo */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Image className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground">
              Brand Logo
            </h2>
            <p className="text-sm text-muted-foreground">
              Upload your logo to appear on all documents (max 500KB)
            </p>
          </div>
        </div>

        <div className="space-y-4">
          {/* Logo Preview */}
          {(profile.logo_data || profile.logo_url) && (
            <div className="flex items-center gap-4 p-4 bg-muted/50 rounded-lg">
              <img 
                src={profile.logo_data || profile.logo_url} 
                alt="Brand Logo" 
                className="h-20 w-auto max-w-[240px] object-contain"
                data-testid="logo-preview"
              />
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={removeLogo}
                className="text-destructive hover:text-destructive"
                data-testid="remove-logo-btn"
              >
                <X className="w-4 h-4 mr-1" /> Remove
              </Button>
            </div>
          )}

          {/* Upload Button */}
          <div className="flex items-center gap-4">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleLogoUpload}
              className="hidden"
              data-testid="logo-file-input"
            />
            <Button
              variant="outline"
              onClick={() => fileInputRef.current?.click()}
              data-testid="upload-logo-btn"
            >
              <Upload className="w-4 h-4 mr-2" />
              Upload Logo
            </Button>
            <span className="text-sm text-muted-foreground">or</span>
            <Input
              type="url"
              value={profile.logo_url}
              onChange={(e) => setProfile(prev => ({ ...prev, logo_url: e.target.value, logo_data: "" }))}
              placeholder="Paste logo URL"
              className="flex-1 max-w-sm"
              data-testid="logo-url-input"
            />
          </div>
        </div>
      </div>

      {/* Platform Theme - NEW SECTION */}
      <div className="bg-white rounded-2xl border border-slate-100 shadow-card p-6 mb-6" data-testid="platform-theme-section">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
            <Monitor className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-slate-800">
              Platform Theme
            </h2>
            <p className="text-sm text-slate-500">
              Choose your preferred color scheme for the entire platform
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Theme Presets Grid */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-slate-500 mb-4 block">
              Select a Theme
            </Label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {platformThemes.map((theme) => (
                <button
                  key={theme.id}
                  onClick={() => setTheme(theme.id)}
                  className={`relative p-4 rounded-xl border-2 transition-all duration-200 text-left group hover:shadow-md ${
                    currentTheme.id === theme.id 
                      ? "border-primary bg-primary/5 shadow-sm" 
                      : "border-slate-200 hover:border-slate-300 bg-white"
                  }`}
                  data-testid={`theme-${theme.id}`}
                >
                  {/* Selected indicator */}
                  {currentTheme.id === theme.id && (
                    <div className="absolute top-2 right-2 w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                      <Check className="w-3 h-3 text-white" strokeWidth={3} />
                    </div>
                  )}
                  
                  {/* Color swatches */}
                  <div className="flex gap-1.5 mb-3">
                    <div 
                      className="w-8 h-8 rounded-lg shadow-sm" 
                      style={{ backgroundColor: theme.primary }}
                    />
                    <div 
                      className="w-8 h-8 rounded-lg shadow-sm" 
                      style={{ backgroundColor: theme.accent }}
                    />
                  </div>
                  
                  {/* Theme info */}
                  <p className="font-semibold text-sm text-slate-800">{theme.name}</p>
                  <p className="text-xs text-slate-500 mt-0.5">{theme.description}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Theme */}
          <div className="pt-4 border-t border-slate-100">
            <Label className="text-xs uppercase tracking-wide text-slate-500 mb-4 block">
              Or Create Custom Theme
            </Label>
            <div className="flex flex-wrap items-end gap-4">
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Primary Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customPrimaryColor}
                    onChange={(e) => setCustomPrimaryColor(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer border border-slate-200"
                    data-testid="custom-primary-picker"
                  />
                  <Input
                    value={customPrimaryColor}
                    onChange={(e) => setCustomPrimaryColor(e.target.value)}
                    placeholder="#047857"
                    className="w-28 font-mono text-sm"
                  />
                </div>
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">Accent Color</Label>
                <div className="flex items-center gap-2">
                  <input
                    type="color"
                    value={customAccentColor}
                    onChange={(e) => setCustomAccentColor(e.target.value)}
                    className="w-12 h-10 rounded-lg cursor-pointer border border-slate-200"
                    data-testid="custom-accent-picker"
                  />
                  <Input
                    value={customAccentColor}
                    onChange={(e) => setCustomAccentColor(e.target.value)}
                    placeholder="#D4B068"
                    className="w-28 font-mono text-sm"
                  />
                </div>
              </div>
              <Button 
                onClick={() => {
                  setCustomTheme(customPrimaryColor, customAccentColor);
                  toast.success("Custom theme applied!");
                }}
                className="rounded-full"
                data-testid="apply-custom-theme-btn"
              >
                Apply Custom Theme
              </Button>
            </div>
          </div>

          {/* Live Preview */}
          <div className="p-4 rounded-xl border border-slate-200 bg-slate-50">
            <Label className="text-xs uppercase tracking-wide text-slate-500 mb-3 block">
              Live Preview
            </Label>
            <div className="flex flex-wrap items-center gap-3">
              <div 
                className="px-4 py-2 rounded-full text-white font-medium text-sm shadow-sm"
                style={{ backgroundColor: currentTheme.primary }}
              >
                Primary Button
              </div>
              <div 
                className="px-4 py-2 rounded-full border-2 font-medium text-sm"
                style={{ borderColor: currentTheme.primary, color: currentTheme.primary }}
              >
                Outline Button
              </div>
              <div 
                className="px-3 py-1.5 rounded-lg text-white font-bold text-sm"
                style={{ backgroundColor: currentTheme.accent }}
              >
                $1,234.00
              </div>
              <div className="flex items-center gap-2 ml-auto">
                <span className="text-xs text-slate-500">Active:</span>
                <span className="font-semibold text-sm" style={{ color: currentTheme.primary }}>
                  {currentTheme.name}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Brand Colors */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-secondary/10 flex items-center justify-center">
            <Palette className="w-5 h-5 text-secondary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground">
              Brand Colors
            </h2>
            <p className="text-sm text-muted-foreground">
              Customize document colors to match your brand
            </p>
          </div>
        </div>

        <div className="space-y-6">
          {/* Preset Colors */}
          <div>
            <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-3 block">
              Quick Presets
            </Label>
            <div className="flex flex-wrap gap-2">
              {presetColors.map((preset) => (
                <button
                  key={preset.name}
                  onClick={() => applyPresetColor(preset)}
                  className="flex items-center gap-2 px-3 py-2 rounded-md border border-border hover:border-primary/50 transition-colors duration-200"
                  data-testid={`preset-${preset.name.toLowerCase()}`}
                >
                  <div className="flex">
                    <div 
                      className="w-4 h-4 rounded-l-sm" 
                      style={{ backgroundColor: preset.primary }}
                    />
                    <div 
                      className="w-4 h-4 rounded-r-sm" 
                      style={{ backgroundColor: preset.secondary }}
                    />
                  </div>
                  <span className="text-sm">{preset.name}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Custom Colors */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Label>Primary Color (Headers, Accents)</Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="color"
                  value={profile.brand_primary_color}
                  onChange={(e) => setProfile(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer border border-border"
                  data-testid="primary-color-picker"
                />
                <Input
                  value={profile.brand_primary_color}
                  onChange={(e) => setProfile(prev => ({ ...prev, brand_primary_color: e.target.value }))}
                  placeholder="#047857"
                  className="flex-1 font-mono"
                  data-testid="primary-color-input"
                />
              </div>
            </div>
            <div>
              <Label>Secondary Color (Highlights, Totals)</Label>
              <div className="flex items-center gap-3 mt-2">
                <input
                  type="color"
                  value={profile.brand_secondary_color}
                  onChange={(e) => setProfile(prev => ({ ...prev, brand_secondary_color: e.target.value }))}
                  className="w-12 h-10 rounded cursor-pointer border border-border"
                  data-testid="secondary-color-picker"
                />
                <Input
                  value={profile.brand_secondary_color}
                  onChange={(e) => setProfile(prev => ({ ...prev, brand_secondary_color: e.target.value }))}
                  placeholder="#B49248"
                  className="flex-1 font-mono"
                  data-testid="secondary-color-input"
                />
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="p-4 rounded-lg border border-border bg-white">
            <Label className="text-xs uppercase tracking-wide text-muted-foreground mb-3 block">
              Preview
            </Label>
            <div className="flex items-center gap-4">
              <div 
                className="px-4 py-2 rounded text-white font-medium text-sm"
                style={{ backgroundColor: profile.brand_primary_color }}
              >
                Primary Button
              </div>
              <div 
                className="px-4 py-2 rounded text-white font-medium text-sm"
                style={{ backgroundColor: profile.brand_secondary_color }}
              >
                $1,234.00
              </div>
              <div 
                className="h-1 flex-1 rounded"
                style={{ backgroundColor: profile.brand_primary_color }}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Default Settings */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <h2 className="font-heading font-semibold text-foreground mb-6">
          Default Settings
        </h2>

        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Label>Transaction Currency</Label>
              <Select 
                value={profile.default_currency} 
                onValueChange={(v) => setProfile(prev => ({ ...prev, default_currency: v }))}
              >
                <SelectTrigger data-testid="profile-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Currency for transactions</p>
            </div>
            <div>
              <Label>Reporting Currency</Label>
              <Select 
                value={profile.reporting_currency || profile.default_currency} 
                onValueChange={(v) => setProfile(prev => ({ ...prev, reporting_currency: v }))}
              >
                <SelectTrigger data-testid="profile-reporting-currency-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map(c => (
                    <SelectItem key={c.code} value={c.code}>
                      {c.symbol} {c.code} - {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-1">Currency for reports (e.g., USD or LRD)</p>
            </div>
            <div>
              <Label>Default Tax Rate (%)</Label>
              <Input
                type="number"
                value={profile.default_tax_rate}
                onChange={(e) => setProfile(prev => ({ 
                  ...prev, 
                  default_tax_rate: parseFloat(e.target.value) || 0 
                }))}
                placeholder="0"
                data-testid="profile-taxrate-input"
              />
            </div>
          </div>

          <div>
            <Label>Default Notes</Label>
            <Textarea
              value={profile.default_notes}
              onChange={(e) => setProfile(prev => ({ ...prev, default_notes: e.target.value }))}
              placeholder="Thank you for your business!"
              rows={2}
              data-testid="profile-notes-input"
            />
          </div>

          <div>
            <Label>Default Terms & Conditions</Label>
            <Textarea
              value={profile.default_terms}
              onChange={(e) => setProfile(prev => ({ ...prev, default_terms: e.target.value }))}
              placeholder="Payment due within 30 days..."
              rows={3}
              data-testid="profile-terms-input"
            />
          </div>
        </div>
      </div>

      {/* E-Signature */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <PenTool className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-foreground">
              E-Signature
            </h2>
            <p className="text-sm text-muted-foreground">
              Create your signature to sign documents before downloading
            </p>
          </div>
        </div>

        <SignaturePad
          value={profile.signature_data}
          onChange={(sig) => setProfile(prev => ({ ...prev, signature_data: sig }))}
          primaryColor={profile.brand_primary_color}
        />
      </div>

      {/* User Management */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Users className="w-5 h-5 text-primary" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-foreground">
                User Management
              </h2>
              <p className="text-sm text-muted-foreground">
                Manage team members and their access ({teamMembers.length + pendingInvitations.length}/3)
              </p>
            </div>
          </div>
          <Button 
            size="sm" 
            onClick={() => setShowInviteDialog(true)}
            disabled={teamMembers.length + pendingInvitations.length >= 3}
            data-testid="invite-user-btn"
          >
            <Plus className="w-4 h-4 mr-1" />
            Invite User
          </Button>
        </div>

        {loadingTeam ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : teamMembers.length === 0 && pendingInvitations.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Users className="w-12 h-12 mx-auto mb-3 opacity-30" />
            <p>No team members yet</p>
            <p className="text-sm">Invite users to collaborate on your account</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Active Team Members */}
            {teamMembers.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Active Members ({teamMembers.length})
                </h3>
                <div className="space-y-2">
                  {teamMembers.map((member) => (
                    <div 
                      key={member.id} 
                      className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                          <span className="text-sm font-medium text-green-700">
                            {(member.name || member.email)?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="font-medium text-sm">{member.name || "No name"}</p>
                          <p className="text-xs text-muted-foreground">{member.email}</p>
                          <p className="text-xs text-green-600 capitalize">
                            {member.role || "team_member"} • Active
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => removeTeamMember(member.id)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Pending Invitations */}
            {pendingInvitations.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                  Pending Invitations ({pendingInvitations.length})
                </h3>
                <div className="space-y-2">
                  {pendingInvitations.map((invite) => (
                    <div 
                      key={invite.id} 
                      className="flex items-center justify-between p-3 bg-amber-50 border border-amber-200 rounded-lg"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                          <Mail className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-medium text-sm">{invite.name || "No name"}</p>
                          <p className="text-xs text-muted-foreground">{invite.email}</p>
                          <p className="text-xs text-amber-600">
                            Invitation sent • Waiting to accept
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => resendInvitation(invite.id)}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Resend invitation"
                        >
                          <RefreshCw className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => cancelInvitation(invite.id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          title="Cancel invitation"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Invite Team Member</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Full Name</Label>
              <Input
                type="text"
                placeholder="John Doe"
                value={inviteName}
                onChange={(e) => setInviteName(e.target.value)}
                data-testid="invite-name-input"
              />
            </div>
            <div>
              <Label>Email Address</Label>
              <Input
                type="email"
                placeholder="colleague@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                data-testid="invite-email-input"
              />
            </div>
            <div>
              <Label>Role</Label>
              <Select value={inviteRole} onValueChange={setInviteRole}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="bookkeeper">Bookkeeper</SelectItem>
                  <SelectItem value="viewer">Viewer (Read Only)</SelectItem>
                </SelectContent>
              </Select>
              <div className="mt-2 p-3 bg-muted/50 rounded-lg text-xs text-muted-foreground">
                {inviteRole === "admin" && (
                  <div>
                    <p className="font-medium text-foreground mb-1">Admin - Full Access</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Create & manage all documents</li>
                      <li>Manage clients & inventory</li>
                      <li>View financial reports</li>
                      <li>Access company settings</li>
                      <li>Invite & remove team members</li>
                    </ul>
                  </div>
                )}
                {inviteRole === "bookkeeper" && (
                  <div>
                    <p className="font-medium text-foreground mb-1">Bookkeeper - Data Entry</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>Create invoices, quotes & receipts</li>
                      <li>Process sales & transactions</li>
                      <li>Manage clients & inventory</li>
                      <li className="text-red-500">Cannot view financial reports</li>
                      <li className="text-red-500">Cannot access settings</li>
                    </ul>
                  </div>
                )}
                {inviteRole === "viewer" && (
                  <div>
                    <p className="font-medium text-foreground mb-1">Viewer - Read Only</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>View documents only</li>
                      <li className="text-red-500">Cannot create or edit anything</li>
                      <li className="text-red-500">Cannot access reports or settings</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowInviteDialog(false)}>
              Cancel
            </Button>
            <Button onClick={inviteTeamMember} disabled={inviting || !inviteEmail || !inviteName}>
              {inviting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                "Send Invitation"
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button 
          onClick={saveProfile} 
          disabled={saving}
          className="bg-secondary hover:bg-secondary/90"
          data-testid="save-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Privacy Notice */}
      <div className="mt-8 p-4 bg-muted/50 rounded-lg">
        <h3 className="font-heading font-semibold text-sm text-foreground mb-2">
          Privacy Notice
        </h3>
        <p className="text-sm text-muted-foreground">
          Your documents are stored locally in your browser by default. 
          Business profile settings are saved to enable document pre-filling. 
          We do not share your data with third parties.
        </p>
      </div>

      {/* Support Section */}
      <div className="bg-card rounded-lg border border-border p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <h2 className="font-heading font-semibold text-foreground">
                Support
              </h2>
              <p className="text-sm text-muted-foreground">
                Need help? Submit a support ticket
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setShowTicketDialog(true)}
            data-testid="new-ticket-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            New Ticket
          </Button>
        </div>

        {/* My Tickets */}
        {loadingTickets ? (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading tickets...
          </div>
        ) : supportTickets.length > 0 ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground mb-2">Your support tickets:</p>
            {supportTickets.map((ticket) => (
              <div key={ticket.id} className="p-4 bg-muted/50 rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium text-foreground">{ticket.subject}</p>
                    <p className="text-sm text-muted-foreground line-clamp-2 mt-1">{ticket.message}</p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    ticket.status === "open" ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                    ticket.status === "in_progress" ? "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" :
                    "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400"
                  }`}>
                    {ticket.status === "in_progress" ? "In Progress" : ticket.status}
                  </span>
                </div>
                {ticket.responses && ticket.responses.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-border">
                    <p className="text-xs text-muted-foreground mb-1">Latest response:</p>
                    <p className="text-sm text-foreground">{ticket.responses[ticket.responses.length - 1].message}</p>
                  </div>
                )}
                <p className="text-xs text-muted-foreground mt-2">
                  {new Date(ticket.created_at).toLocaleDateString()} • Priority: {ticket.priority}
                </p>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-muted-foreground text-sm">No support tickets yet. Click "New Ticket" if you need assistance.</p>
        )}
      </div>

      {/* Support Ticket Dialog */}
      <Dialog open={showTicketDialog} onOpenChange={setShowTicketDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Submit Support Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Subject</Label>
              <Input 
                placeholder="Brief description of your issue" 
                value={ticketSubject}
                onChange={(e) => setTicketSubject(e.target.value)}
                data-testid="ticket-subject-input"
              />
            </div>
            <div>
              <Label>Priority</Label>
              <Select value={ticketPriority} onValueChange={setTicketPriority}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - General question</SelectItem>
                  <SelectItem value="normal">Normal - Need assistance</SelectItem>
                  <SelectItem value="high">High - Affecting my work</SelectItem>
                  <SelectItem value="urgent">Urgent - Critical issue</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Message</Label>
              <Textarea 
                placeholder="Describe your issue in detail. Include any error messages or steps to reproduce the problem."
                value={ticketMessage}
                onChange={(e) => setTicketMessage(e.target.value)}
                className="min-h-[120px]"
                data-testid="ticket-message-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTicketDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitSupportTicket} 
              disabled={submittingTicket}
              data-testid="submit-ticket-btn"
            >
              {submittingTicket ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Submit Ticket
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Danger Zone - Reset Account */}
      <div className="mt-8 p-6 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
            <Trash2 className="w-5 h-5 text-red-600" />
          </div>
          <div>
            <h2 className="font-heading font-semibold text-red-800">
              Danger Zone
            </h2>
            <p className="text-sm text-red-600">
              Permanently delete all your account data
            </p>
          </div>
        </div>
        <p className="text-sm text-red-700 mb-4">
          This will permanently delete all your transactions, documents, clients, inventory, and sales data. 
          Your account and subscription will remain active, but all business data will be erased. 
          This action cannot be undone.
        </p>
        <Button 
          variant="destructive"
          onClick={handleResetAccount}
          disabled={resettingAccount}
          data-testid="reset-account-btn"
        >
          {resettingAccount ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Resetting...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Reset Account Data
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
